import React, { useState, useCallback } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Button,
    TextField,
    Typography,
    Box,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Icon,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Switch,
    FormControlLabel
} from '@material-ui/core';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Edit as EditIcon,
    Apps as AppsIcon,
    OpenInNew as OpenInNewIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    cardHeader: {
        paddingBottom: '8px',
        backgroundColor: '#f7f9fc'
    },
    addButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        width: '100%',
        minHeight: theme.spacing(8),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
    applicationCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows[2],
        },
    },
    applicationHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#f7f9fc',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    applicationInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flex: 1,
    },
    applicationIcon: {
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
    },
    applicationDetails: {
        flex: 1,
    },
    applicationActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    categoryChip: {
        marginLeft: theme.spacing(1),
        height: 20,
        fontSize: '0.7rem',
    },
    urlPreview: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontFamily: 'monospace',
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }
}));

// Available icon options for applications
const iconOptions = [
    { value: 'apps', label: 'Apps', icon: 'apps' },
    { value: 'jupyter', label: 'Jupyter', imgSrc: 'assets/images/logos/jupyter.svg' },
    { value: 'code', label: 'Code', icon: 'code' },
    { value: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { value: 'terminal', label: 'Terminal', icon: 'terminal' },
    { value: 'analytics', label: 'Analytics', icon: 'analytics' },
    { value: 'memory', label: 'Memory/AI', icon: 'memory' },
    { value: 'assessment', label: 'Assessment', icon: 'assessment' },
    { value: 'science', label: 'Science', icon: 'science' },
    { value: 'computer', label: 'Computer', icon: 'computer' },
    { value: 'storage', label: 'Storage', icon: 'storage' },
    { value: 'cloud', label: 'Cloud', icon: 'cloud' },
    { value: 'dns', label: 'DNS/Network', icon: 'dns' },
    { value: 'layers', label: 'Layers', icon: 'layers' },
    { value: 'data_usage', label: 'Data', icon: 'data_usage' },
    { value: 'insert_chart', label: 'Charts', icon: 'insert_chart' },
    { value: 'school', label: 'Education', icon: 'school' },
    { value: 'settings', label: 'Settings', icon: 'settings' },
    { value: 'web', label: 'Web', icon: 'web' },
];

// Map of icon values to image sources for custom icons
const customIconImages = {
    jupyter: 'assets/images/logos/jupyter.svg',
};

// Category options
const categoryOptions = [
    { value: 'general', label: 'General' },
    { value: 'monitoring', label: 'Monitoring' },
    { value: 'development', label: 'Development' },
    { value: 'ai', label: 'AI/ML' },
    { value: 'data', label: 'Data Science' },
    { value: 'admin', label: 'Administration' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' },
];

// Launch type options
const launchTypeOptions = [
    { value: 'url', label: 'Open URL', description: 'Opens a web URL in a new tab' },
    { value: 'instructions', label: 'Show Instructions', description: 'Shows instructions dialog with action button' },
    { value: 'proxied-app', label: 'Proxied Application', description: 'Launches app on cluster and proxies through Heidi (no VNC needed)' },
];

// Action type options (for instructions launch type)
const actionTypeOptions = [
    { value: 'access-tab', label: 'Go to Access Tab', description: 'Navigate to cluster Access tab' },
    { value: 'remote-desktop', label: 'Open Remote Desktop', description: 'Launch VNC remote desktop directly' },
    { value: 'none', label: 'No Action', description: 'Just show instructions without action button' },
];

// Generate unique ID
const generateId = () => {
    return 'app-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

const sectionCardStyle = {
    marginBottom: '24px',
    boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'visible',
};

const sectionCardHeaderStyle = {
    paddingBottom: '8px',
    backgroundColor: '#f7f9fc',
};

const sectionTitleFontStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f2147',
};

const ClusterApplicationsSection = ({
    applications = [],
    onChange,
    disabled = false,
}) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        url: '',
        icon: 'apps',
        description: '',
        category: 'general',
        instructions: '',
        enabled: true,
        launchType: 'url',        // 'url', 'instructions', or 'proxied-app'
        actionType: 'access-tab', // 'access-tab', 'remote-desktop', or 'none'
        actionButtonLabel: '',    // Custom label for action button
        ssoEnabled: false,        // Feature: sso-classifyx - Enable Heidi SSO token injection
        appId: '',                // Feature: sso-classifyx - SSO app identifier (e.g., "classifyx")
        // Feature: app-data-cleanup - delete matching files from the user's home dir on stop
        cleanupOnStop: false,
        cleanupPatterns: '',
        // Feature: cluster-app-proxy - Proxied app configuration
        proxyConfig: {
            command: '',
            defaultPort: 0,
            basePath: '/',
            healthCheckPath: '',
            env: {},
            singleInstance: true,
            singularityImage: '',
            containerBinds: [],
            pathMode: ''
        }
    });
    const [errors, setErrors] = useState({});

    // Validate form
    const validateForm = useCallback(() => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        // Validation depends on launch type
        if (formData.launchType === 'url') {
            // URL is required for URL launch type
            if (!formData.url.trim()) {
                newErrors.url = 'URL is required';
            }
        } else if (formData.launchType === 'instructions') {
            // Instructions are required for instructions launch type
            if (!formData.instructions.trim()) {
                newErrors.instructions = 'Instructions are required for this launch type';
            }
        } else if (formData.launchType === 'proxied-app') {
            // Command is required for proxied app launch type
            if (!formData.proxyConfig.command.trim()) {
                newErrors.proxyCommand = 'Command is required for proxied applications';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    // Handle opening the add/edit dialog
    const handleOpenDialog = (app = null) => {
        if (app) {
            setEditingApp(app);
            setFormData({
                id: app.id,
                name: app.name || '',
                url: app.url || '',
                icon: app.icon || 'apps',
                description: app.description || '',
                category: app.category || 'general',
                instructions: app.instructions || '',
                enabled: app.enabled !== false,
                launchType: app.launchType || 'url',
                actionType: app.actionType || 'access-tab',
                actionButtonLabel: app.actionButtonLabel || '',
                ssoEnabled: app.ssoEnabled || false,
                appId: app.appId || '',
                cleanupOnStop: app.cleanupOnStop || false,
                cleanupPatterns: app.cleanupPatterns || '',
                proxyConfig: {
                    command: app.proxyConfig?.command || '',
                    defaultPort: app.proxyConfig?.defaultPort || 0,
                    basePath: app.proxyConfig?.basePath || '/',
                    healthCheckPath: app.proxyConfig?.healthCheckPath || '',
                    env: app.proxyConfig?.env || {},
                    singleInstance: app.proxyConfig?.singleInstance !== false,
                    singularityImage: app.proxyConfig?.singularityImage || '',
                    containerBinds: app.proxyConfig?.containerBinds || [],
                    pathMode: app.proxyConfig?.pathMode || ''
                }
            });
        } else {
            setEditingApp(null);
            setFormData({
                id: generateId(),
                name: '',
                url: '',
                icon: 'apps',
                description: '',
                category: 'general',
                instructions: '',
                enabled: true,
                launchType: 'url',
                actionType: 'access-tab',
                actionButtonLabel: '',
                ssoEnabled: false,
                appId: '',
                cleanupOnStop: false,
                cleanupPatterns: '',
                proxyConfig: {
                    command: '',
                    defaultPort: 0,
                    basePath: '/',
                    healthCheckPath: '',
                    env: {},
                    singleInstance: true,
                    singularityImage: '',
                    containerBinds: [],
                    pathMode: ''
                }
            });
        }
        setErrors({});
        setDialogOpen(true);
    };

    // Handle closing the dialog
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingApp(null);
        setErrors({});
    };

    // Handle saving an application
    const handleSaveApplication = () => {
        if (!validateForm()) {
            return;
        }

        const newApp = {
            id: formData.id,
            name: formData.name.trim(),
            url: formData.url.trim(),
            icon: formData.icon,
            description: formData.description.trim(),
            category: formData.category,
            instructions: formData.instructions.trim(),
            enabled: formData.enabled,
            launchType: formData.launchType,
            actionType: formData.actionType,
            actionButtonLabel: formData.actionButtonLabel.trim(),
            ssoEnabled: formData.ssoEnabled,
            appId: formData.appId.trim(),
            cleanupOnStop: formData.launchType === 'proxied-app' && !!formData.cleanupOnStop,
            cleanupPatterns: formData.launchType === 'proxied-app' && formData.cleanupOnStop
                ? (formData.cleanupPatterns || '').trim()
                : ''
        };

        // Include proxyConfig for proxied-app launch type
        if (formData.launchType === 'proxied-app') {
            newApp.proxyConfig = {
                command: formData.proxyConfig.command.trim(),
                defaultPort: parseInt(formData.proxyConfig.defaultPort, 10) || 0,
                basePath: formData.proxyConfig.basePath.trim() || '/',
                healthCheckPath: formData.proxyConfig.healthCheckPath.trim(),
                env: formData.proxyConfig.env || {},
                singleInstance: formData.proxyConfig.singleInstance,
                singularityImage: formData.proxyConfig.singularityImage.trim(),
                containerBinds: formData.proxyConfig.containerBinds.filter(b => b.trim()),
                pathMode: formData.proxyConfig.pathMode || ''
            };
        }

        let updatedApps;
        if (editingApp) {
            // Update existing
            updatedApps = applications.map(app => 
                app.id === editingApp.id ? newApp : app
            );
        } else {
            // Add new
            updatedApps = [...applications, newApp];
        }

        onChange(updatedApps);
        handleCloseDialog();
    };

    // Handle deleting an application
    const handleDeleteApplication = (appId) => {
        const updatedApps = applications.filter(app => app.id !== appId);
        onChange(updatedApps);
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user types
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Handle proxyConfig field changes
    const handleProxyConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            proxyConfig: {
                ...prev.proxyConfig,
                [field]: value
            }
        }));
        // Clear proxy-related errors
        if (field === 'command' && errors.proxyCommand) {
            setErrors(prev => ({ ...prev, proxyCommand: null }));
        }
    };

    // Get category label
    const getCategoryLabel = (categoryValue) => {
        const cat = categoryOptions.find(c => c.value === categoryValue);
        return cat ? cat.label : categoryValue;
    };

    return (
        <Card style={sectionCardStyle} className={classes.root}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center">
                            <Icon style={{ marginRight: '8px', color: '#2196F3' }}>apps</Icon>
                            <Typography style={sectionTitleFontStyle}>Cluster Applications</Typography>
                            {applications.length > 0 && (
                                <Chip 
                                    label={applications.length} 
                                    size="small" 
                                    color="primary" 
                                    style={{ marginLeft: 8, height: 20 }}
                                />
                            )}
                        </Box>
                        <IconButton 
                            size="small" 
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                }
                style={sectionCardHeaderStyle}
                className={classes.cardHeader}
            />
            <Collapse in={expanded}>
                <CardContent>
                    {/* Description */}
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Add web applications and services that this cluster provides (e.g., JupyterLab, TensorBoard, WebUI). 
                        These will be accessible from the Applications tab after deployment.
                    </Typography>

                    {/* Applications list */}
                    {applications.length > 0 ? (
                        <Box mb={2}>
                            {applications.map((app, index) => (
                                <Box key={app.id} className={classes.applicationCard}>
                                    <Box className={classes.applicationHeader}>
                                        <Box className={classes.applicationInfo}>
                                            <Box className={classes.applicationIcon}>
                                                <Icon>{app.icon || 'apps'}</Icon>
                                            </Box>
                                            <Box className={classes.applicationDetails}>
                                                <Box display="flex" alignItems="center">
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                        {app.name}
                                                    </Typography>
                                                    <Chip
                                                        label={getCategoryLabel(app.category)}
                                                        size="small"
                                                        className={classes.categoryChip}
                                                    />
                                                    {app.launchType === 'instructions' && (
                                                        <Chip
                                                            label="Instructions"
                                                            size="small"
                                                            color="secondary"
                                                            className={classes.categoryChip}
                                                            icon={<Icon style={{ fontSize: 12 }}>info</Icon>}
                                                        />
                                                    )}
                                                    {app.launchType === 'proxied-app' && (
                                                        <Chip
                                                            label="Proxied"
                                                            size="small"
                                                            style={{ backgroundColor: '#4caf50', color: '#fff' }}
                                                            className={classes.categoryChip}
                                                            icon={<Icon style={{ fontSize: 12, color: '#fff' }}>cloud</Icon>}
                                                        />
                                                    )}
                                                    {app.ssoEnabled && (
                                                        <Chip
                                                            label="SSO"
                                                            size="small"
                                                            color="primary"
                                                            className={classes.categoryChip}
                                                            icon={<Icon style={{ fontSize: 12 }}>vpn_key</Icon>}
                                                        />
                                                    )}
                                                    {!app.enabled && (
                                                        <Chip
                                                            label="Disabled"
                                                            size="small"
                                                            color="default"
                                                            className={classes.categoryChip}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography className={classes.urlPreview}>
                                                    {app.launchType === 'instructions'
                                                        ? `Action: ${actionTypeOptions.find(a => a.value === app.actionType)?.label || 'Show Instructions'}`
                                                        : app.launchType === 'proxied-app'
                                                        ? `Command: ${app.proxyConfig?.command || 'Not configured'}`
                                                        : app.url
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.applicationActions}>
                                            <Tooltip title="Edit">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleOpenDialog(app)}
                                                    disabled={disabled}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleDeleteApplication(app.id)}
                                                    disabled={disabled}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    {app.description && (
                                        <Box p={1.5} pt={1}>
                                            <Typography variant="body2" color="textSecondary">
                                                {app.description}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box className={classes.emptyState} mb={2}>
                            <AppsIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
                            <Typography variant="body2">
                                No applications defined yet. Click below to add one.
                            </Typography>
                        </Box>
                    )}

                    {/* Add button */}
                    <Button
                        className={classes.addButton}
                        onClick={() => handleOpenDialog()}
                        disabled={disabled}
                    >
                        <AddIcon style={{ marginRight: 8 }} />
                        Add Application
                    </Button>
                </CardContent>
            </Collapse>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Icon style={{ marginRight: 8, color: '#2196F3' }}>
                            {editingApp ? 'edit' : 'add_circle'}
                        </Icon>
                        {editingApp ? 'Edit Application' : 'Add Application'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Name */}
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Application Name"
                                value={formData.name}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name || 'e.g., JupyterLab, TensorBoard, WebUI'}
                                margin="dense"
                                variant="outlined"
                                required
                            />
                        </Grid>

                        {/* Icon */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Icon</InputLabel>
                                <Select
                                    value={formData.icon}
                                    onChange={(e) => handleFieldChange('icon', e.target.value)}
                                    label="Icon"
                                >
                                    {iconOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Box display="flex" alignItems="center">
                                                {opt.imgSrc
                                                    ? <img src={opt.imgSrc} alt={opt.label} style={{ width: 20, height: 20, marginRight: 8 }} />
                                                    : <Icon style={{ marginRight: 8, fontSize: 20 }}>{opt.icon}</Icon>
                                                }
                                                {opt.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Launch Type */}
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Launch Type</InputLabel>
                                <Select
                                    value={formData.launchType}
                                    onChange={(e) => handleFieldChange('launchType', e.target.value)}
                                    label="Launch Type"
                                >
                                    {launchTypeOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Box>
                                                <Typography variant="body2">{opt.label}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {opt.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {formData.launchType === 'url' 
                                        ? 'Opens a web URL when clicked'
                                        : 'Shows an instructions dialog with an action button'
                                    }
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* URL Section - Only shown for URL launch type */}
                        {formData.launchType === 'url' && (
                            <>
                                <Grid item xs={12}>
                                    <Box mb={1} p={1.5} bgcolor="#e3f2fd" borderRadius={4}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>💡 Tip:</strong> Use <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: 2 }}>&lt;server-ip&gt;</code> as a placeholder - it will be replaced with the cluster's IP after deployment.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="URL"
                                        value={formData.url}
                                        onChange={(e) => handleFieldChange('url', e.target.value)}
                                        error={!!errors.url}
                                        helperText={errors.url || 'e.g., http://<server-ip>:8888/lab'}
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="http://<server-ip>:8888"
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <Tooltip title="Insert http://<server-ip>: template">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => {
                                                            if (!formData.url) {
                                                                handleFieldChange('url', 'http://<server-ip>:');
                                                            } else if (!formData.url.includes('<server-ip>')) {
                                                                handleFieldChange('url', 'http://<server-ip>:' + formData.url.replace(/^https?:\/\//, ''));
                                                            }
                                                        }}
                                                    >
                                                        <Icon fontSize="small">add_location</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Instructions Section - Only shown for Instructions launch type */}
                        {formData.launchType === 'instructions' && (
                            <>
                                <Grid item xs={12}>
                                    <Box mb={1} p={1.5} bgcolor="#fff3e0" borderRadius={4}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>📋 Instructions Mode:</strong> When users click this application, they will see a dialog with your instructions and an action button. 
                                            This is ideal for applications that need to be run inside the VNC desktop session (e.g., E4S, terminal commands).
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Action Type */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Action Button</InputLabel>
                                        <Select
                                            value={formData.actionType}
                                            onChange={(e) => handleFieldChange('actionType', e.target.value)}
                                            label="Action Button"
                                        >
                                            {actionTypeOptions.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    <Box>
                                                        <Typography variant="body2">{opt.label}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {actionTypeOptions.find(a => a.value === formData.actionType)?.description}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Custom Action Button Label */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Action Button Label (optional)"
                                        value={formData.actionButtonLabel}
                                        onChange={(e) => handleFieldChange('actionButtonLabel', e.target.value)}
                                        helperText={`Default: "${formData.actionType === 'remote-desktop' ? 'Open Remote Desktop' : formData.actionType === 'access-tab' ? 'Go to Access Tab' : 'Close'}"`}
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="Custom button text"
                                    />
                                </Grid>

                                {/* Instructions - Required for this mode */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Instructions (HTML supported)"
                                        value={formData.instructions}
                                        onChange={(e) => handleFieldChange('instructions', e.target.value)}
                                        error={!!errors.instructions}
                                        helperText={errors.instructions || 'Enter detailed instructions. HTML tags like <b>, <code>, <ul>, <li> are supported.'}
                                        margin="dense"
                                        variant="outlined"
                                        multiline
                                        rows={6}
                                        required
                                        placeholder="<h4>How to access E4S</h4>\n<ol>\n  <li>Open a terminal in the remote desktop</li>\n  <li>Run: <code>e4s-cl init</code></li>\n  <li>Follow the prompts...</li>\n</ol>"
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Proxied App Section - Only shown for proxied-app launch type */}
                        {formData.launchType === 'proxied-app' && (
                            <>
                                <Grid item xs={12}>
                                    <Box mb={1} p={1.5} bgcolor="#e8f5e9" borderRadius={4}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>Proxied Application:</strong> The application will be launched on the cluster head node and proxied through Heidi's domain with SSL.
                                            Users access it directly in their browser — no VNC required. Ideal for Jupyter Labs, RStudio, VS Code Server, and similar web apps.
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Command */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Launch Command"
                                        value={formData.proxyConfig.command}
                                        onChange={(e) => handleProxyConfigChange('command', e.target.value)}
                                        error={!!errors.proxyCommand}
                                        helperText={errors.proxyCommand || 'Command to start the application (e.g., "jupyter lab", "/usr/lib/rstudio-server/bin/rserver", "code-server")'}
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="jupyter lab"
                                        required
                                    />
                                </Grid>

                                {/* Base Path and Port */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Base Path"
                                        value={formData.proxyConfig.basePath}
                                        onChange={(e) => handleProxyConfigChange('basePath', e.target.value)}
                                        helperText='URL base path (e.g., "/lab" for Jupyter, "/" for RStudio)'
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="/lab"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Preferred Port (0 = auto)"
                                        type="number"
                                        value={formData.proxyConfig.defaultPort || ''}
                                        onChange={(e) => handleProxyConfigChange('defaultPort', e.target.value)}
                                        helperText="Port to run on (0 or empty for auto-assign from 9000-9099)"
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="0"
                                    />
                                </Grid>

                                {/* Path handling: apps disagree on whether the proxy
                                    prefix should reach them. Getting this wrong makes
                                    every request 404, so it is worth surfacing. */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Proxy Path Handling</InputLabel>
                                        <Select
                                            value={formData.proxyConfig.pathMode || ''}
                                            onChange={(e) => handleProxyConfigChange('pathMode', e.target.value)}
                                            label="Proxy Path Handling"
                                        >
                                            <MenuItem value="">Auto-detect (recommended)</MenuItem>
                                            <MenuItem value="prefix">Keep prefix: app is told its own URL prefix (Jupyter)</MenuItem>
                                            <MenuItem value="strip">Strip prefix: app expects the proxy to remove it (RStudio)</MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            Jupyter serves itself from the proxy path and wants it left on;
                                            RStudio uses it only to build links and 404s unless it is removed
                                            first. Auto-detect picks Strip for an <code>rserver</code> command
                                            and Keep for everything else.
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Singularity Container */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Singularity Image (optional)"
                                        value={formData.proxyConfig.singularityImage}
                                        onChange={(e) => handleProxyConfigChange('singularityImage', e.target.value)}
                                        helperText="Path to .sif container image (e.g., /e4s/containers/jupyter.sif). Leave empty for native execution."
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="/path/to/container.sif"
                                    />
                                </Grid>

                                {/* Container Binds */}
                                {formData.proxyConfig.singularityImage && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Container Bind Mounts (comma-separated)"
                                            value={(formData.proxyConfig.containerBinds || []).join(', ')}
                                            onChange={(e) => handleProxyConfigChange(
                                                'containerBinds',
                                                e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            )}
                                            helperText="Directories to bind into the container (e.g., /e4s:/e4s, /data:/data)"
                                            margin="dense"
                                            variant="outlined"
                                            placeholder="/e4s:/e4s, /data:/data"
                                        />
                                    </Grid>
                                )}

                                {/* Single Instance */}
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.proxyConfig.singleInstance}
                                                onChange={(e) => handleProxyConfigChange('singleInstance', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Single Instance Per User"
                                    />
                                    <FormHelperText style={{ marginTop: -4 }}>
                                        Reuse existing session if one is already running
                                    </FormHelperText>
                                </Grid>

                                {/* Feature: app-data-cleanup */}
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!formData.cleanupOnStop}
                                                onChange={(e) => handleFieldChange('cleanupOnStop', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Delete app data on close"
                                    />
                                    <FormHelperText style={{ marginTop: -4 }}>
                                        When the app is stopped, delete the matching files from the user's home directory on the head node.
                                    </FormHelperText>
                                    {formData.cleanupOnStop && (
                                        <TextField
                                            fullWidth
                                            margin="dense"
                                            variant="outlined"
                                            label="Files / extensions to delete (comma-separated)"
                                            placeholder="*.ipynb, *.tmp"
                                            value={formData.cleanupPatterns || ''}
                                            onChange={(e) => handleFieldChange('cleanupPatterns', e.target.value)}
                                            helperText="Recursively deleted from the user's home dir on stop. e.g. *.ipynb removes all notebooks."
                                        />
                                    )}
                                </Grid>
                            </>
                        )}

                        {/* Category */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={formData.category}
                                    onChange={(e) => handleFieldChange('category', e.target.value)}
                                    label="Category"
                                >
                                    {categoryOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* SSO Authentication - Feature: sso-classifyx */}
                        {formData.launchType === 'url' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.ssoEnabled}
                                                onChange={(e) => handleFieldChange('ssoEnabled', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Enable SSO Authentication"
                                    />
                                    <FormHelperText style={{ marginTop: -4 }}>
                                        Injects a Heidi SSO token into the URL on launch
                                    </FormHelperText>
                                </Grid>
                                {formData.ssoEnabled && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="SSO App ID"
                                            value={formData.appId}
                                            onChange={(e) => handleFieldChange('appId', e.target.value)}
                                            helperText='Identifier for token generation (e.g., "classifyx")'
                                            margin="dense"
                                            variant="outlined"
                                            placeholder="classifyx"
                                            required
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleFieldChange('description', e.target.value)}
                                helperText="Brief description of the application"
                                margin="dense"
                                variant="outlined"
                                multiline
                                rows={2}
                            />
                        </Grid>

                        {/* Instructions - Only for URL launch type (optional) */}
                        {formData.launchType === 'url' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Pre-Launch Instructions (optional, HTML supported)"
                                    value={formData.instructions}
                                    onChange={(e) => handleFieldChange('instructions', e.target.value)}
                                    helperText="If provided, users will see these instructions before launching the application. HTML tags like <b>, <code>, <ul>, <li> are supported."
                                    margin="dense"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    placeholder="<h4>Getting Started</h4>\n<p>Login with your HCC credentials. Default port is 8080.</p>"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveApplication} 
                        color="primary" 
                        variant="contained"
                    >
                        {editingApp ? 'Save Changes' : 'Add Application'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default ClusterApplicationsSection;
